import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";
import {
  DEFAULT_FONT_OPTIONS,
  DEFAULT_FONT_SETTINGS,
} from "../../types/FontSettings";

const SettingsContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 400px;
  max-width: 100vw;
  background: var(--color-white);
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transform: translateX(${(props) => (props.isOpen ? "0" : "100%")});
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SettingsHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SettingsTitle = styled.h2`
  font-size: 1.25rem;
  font-family: var(--font-heading);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--color-text-light);
  padding: 0.25rem;

  &:hover {
    color: var(--color-text);
  }
`;

const SettingsContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--color-text);
`;

const CurrentColor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--color-bg);
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const ColorSwatch = styled.div`
  width: 48px;
  height: 48px;
  background: ${(props) => props.color};
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const ColorInfo = styled.div`
  flex: 1;
`;

const ColorLabel = styled.div`
  font-size: 0.875rem;
  color: var(--color-text-light);
  margin-bottom: 0.25rem;
`;

const ColorName = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
`;

const ColorOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
`;

const ColorOptionWrapper = styled.div`
  position: relative;

  &:hover .delete-btn {
    opacity: 1;
  }
`;

const ColorOption = styled.button`
  width: 100%;
  height: 40px;
  border: 2px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  background: ${(props) => props.color};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.color}40;
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ff4444;
  color: white;
  border: 2px solid white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background: #ff2222;
  }
`;

const AddColorButton = styled.button`
  width: 100%;
  height: 40px;
  border: 2px dashed var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  background: transparent;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-light);
  font-size: 1.2rem;

  &:hover {
    border-color: var(--color-accent);
    color: var(--color-accent);
    background: var(--color-bg);
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-text);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  pointer-events: none;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: opacity 0.2s ease;
  margin-bottom: 0.25rem;
  z-index: 10;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: var(--color-text);
  }
`;

const ColorPickerModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-white);
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 1100;
  display: ${(props) => (props.isOpen ? "block" : "none")};
`;

const ColorPickerBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: ${(props) => (props.isOpen ? "block" : "none")};
`;

const ColorPickerTitle = styled.h4`
  margin-bottom: 1rem;
  font-family: var(--font-heading);
`;

const ColorInput = styled.input`
  width: 100%;
  height: 60px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 1rem;

  &:hover {
    border-color: var(--color-accent);
  }
`;

const ColorPickerButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const PickerButton = styled.button`
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &.primary {
    background: var(--color-accent);
    color: white;

    &:hover {
      background: var(--color-accent-hover);
    }
  }

  &.secondary {
    background: transparent;
    color: var(--color-text);
    border: 1px solid var(--color-border);

    &:hover {
      background: var(--color-bg);
    }
  }
`;

const FontSizeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FontSizeButton = styled.button`
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-border);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FontSizeDisplay = styled.span`
  font-weight: 500;
  min-width: 3rem;
  text-align: center;
`;

const FontSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  background: var(--color-white);
  border: 2px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  font-family: var(--font-body);
  cursor: pointer;
  transition: border-color 0.2s ease;
  margin-bottom: 0.5rem;

  &:hover {
    border-color: var(--color-text-light);
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const FontCategorySection = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--color-bg);
  border-radius: 8px;
  border: 1px solid var(--color-border);
`;

const FontCategoryTitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--color-text);
  font-family: var(--font-ui);
`;

const FontPreview = styled.div`
  padding: 0.75rem;
  background: var(--color-white);
  border-radius: 4px;
  border: 1px solid var(--color-border);
  margin-top: 0.5rem;
  font-family: ${(props) => props.fontFamily};
  color: var(--color-text-light);
  font-size: ${(props) =>
    props.isDisplay ? "1.25rem" : props.isHeading ? "1.1rem" : "1rem"};
  font-weight: ${(props) =>
    props.isDisplay || props.isHeading ? "600" : "400"};
`;

const ResetButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-bg);
  }
`;

const CustomFontSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: var(--color-bg);
  border-radius: 8px;
  border: 1px solid var(--color-border);
`;

const FileInputWrapper = styled.div`
  margin-bottom: 1rem;
`;

const FileInputLabel = styled.label`
  display: block;
  padding: 0.75rem;
  background: var(--color-white);
  border: 2px dashed var(--color-border);
  border-radius: 6px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--color-text-light);

  &:hover {
    border-color: var(--color-accent);
    color: var(--color-accent);
  }

  input {
    display: none;
  }
`;

const CustomFontsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const CustomFontItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  background: var(--color-white);
  border-radius: 4px;
  font-size: 0.875rem;
`;

const CustomFontName = styled.span`
  flex: 1;
  font-family: ${(props) => props.fontFamily};
`;

const RemoveFontButton = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`;

const FontUploadInfo = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-light);
  margin-top: 0.5rem;
  text-align: center;
`;

const AdvancedSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid var(--color-border);
`;

const CollapsibleHeader = styled.button`
  width: 100%;
  padding: 1rem;
  background: var(--color-white);
  border: 2px solid #0066cc;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 102, 204, 0.05);
  }

  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease;
    transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0)")};
  }
`;

const CollapsibleContent = styled.div`
  margin-top: 1rem;
  display: ${(props) => (props.isOpen ? "block" : "none")};
`;

const DangerZone = styled.div`
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const DangerWarning = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  color: #dc3545;
  font-size: 0.9rem;
  line-height: 1.5;

  .icon {
    font-size: 1.2rem;
    flex-shrink: 0;
    margin-top: -0.1rem;
  }

  strong {
    font-weight: 600;
  }
`;

const DangerButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.75rem;

  &:hover {
    background: #c82333;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ConfirmDialog = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-white);
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 1200;
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

const ConfirmBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1150;
`;

const ConfirmTitle = styled.h3`
  margin-bottom: 1rem;
  font-size: 1.25rem;
  color: #dc3545;
`;

const ConfirmText = styled.p`
  margin-bottom: 1.5rem;
  color: var(--color-text);
  line-height: 1.5;
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  padding: 0.5rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &.cancel {
    background: transparent;
    color: var(--color-text);
    border: 1px solid var(--color-border);

    &:hover {
      background: var(--color-bg);
    }
  }

  &.confirm {
    background: #dc3545;
    color: white;

    &:hover {
      background: #c82333;
    }
  }
`;

// Arrow icon for collapsible
const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <title>Toggle</title>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const defaultColorOptions = [
  { name: "Crimson", value: "#DC143C", hover: "#B91C3C" },
  { name: "Ruby", value: "#E0115F", hover: "#C60E50" },
  { name: "Vermillion", value: "#E34234", hover: "#D63525" },
  { name: "Rust", value: "#B7410E", hover: "#9A360C" },
  { name: "Maroon", value: "#800020", hover: "#660019" },
  { name: "Coral", value: "#FF6B6B", hover: "#FF5252" },
  { name: "Rose", value: "#C73659", hover: "#B12A4C" },
  { name: "Wine", value: "#722F37", hover: "#5C252C" },
  { name: "Blue", value: "#0066cc", hover: "#0052a3" },
  { name: "Sage", value: "#87A96B", hover: "#6F8C57" },
  { name: "Plum", value: "#8E4585", hover: "#7A3B73" },
];

// Using imported font options from types file

// Helper function to darken a color for hover state
const darkenColor = (color, amount = 0.2) => {
  const num = parseInt(color.replace("#", ""), 16);
  const r = Math.max(0, (num >> 16) - Math.round(255 * amount));
  const g = Math.max(0, ((num >> 8) & 0x00ff) - Math.round(255 * amount));
  const b = Math.max(0, (num & 0x0000ff) - Math.round(255 * amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

const Settings = ({ isOpen, onClose }) => {
  const [selectedColor, setSelectedColor] = useState(defaultColorOptions[6]); // Rose as default
  const [fontSize, setFontSize] = useState(18);
  const [fontSettings, setFontSettings] = useState(DEFAULT_FONT_SETTINGS);
  const [hoveredColor, setHoveredColor] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customColor, setCustomColor] = useState("#000000");
  const [customColors, setCustomColors] = useState([]);
  const [colorOptions, setColorOptions] = useState(defaultColorOptions);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [customFonts, setCustomFonts] = useState([]);
  const [availableFonts, setAvailableFonts] = useState(DEFAULT_FONT_OPTIONS);
  const { isAuthorized, currentUser } = useAuth();

  // Save settings to Firestore (global settings)
  const saveSettingsToFirestore = async (settingsData) => {
    // Only authorized users can save settings
    if (!isAuthorized) return;

    try {
      const globalSettingsRef = doc(db, "globalSettings", "siteSettings");
      await setDoc(
        globalSettingsRef,
        {
          ...settingsData,
          updatedAt: new Date().toISOString(),
          lastUpdatedBy: currentUser?.email || "unknown",
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Error saving settings to Firestore:", error);
      // Don't show error toast to user - localStorage is fallback
    }
  };

  // Load saved settings
  useEffect(() => {
    const loadSettings = async () => {
      // Load settings from Firestore (global settings)
      const loadSettingsFromFirestore = async () => {
        try {
          const globalSettingsRef = doc(db, "globalSettings", "siteSettings");
          const settingsDoc = await getDoc(globalSettingsRef);

          if (settingsDoc.exists()) {
            return settingsDoc.data();
          }
          return null;
        } catch (error) {
          console.error("Error loading settings from Firestore:", error);
          return null;
        }
      };

      // Load global settings from Firestore
      const firestoreSettings = await loadSettingsFromFirestore();

      // Load custom fonts
      const savedCustomFonts =
        firestoreSettings?.customFonts ||
        (localStorage.getItem("custom-fonts")
          ? JSON.parse(localStorage.getItem("custom-fonts"))
          : null);

      if (savedCustomFonts) {
        setCustomFonts(savedCustomFonts);

        // Create @font-face rules for each custom font
        savedCustomFonts.forEach((font) => {
          const styleElement = document.createElement("style");
          styleElement.textContent = `
            @font-face {
              font-family: '${font.name}';
              src: url(${font.data});
              font-weight: normal;
              font-style: normal;
            }
          `;
          styleElement.setAttribute("data-font-name", font.name);
          document.head.appendChild(styleElement);
        });

        // Update available fonts
        const customFontOptions = savedCustomFonts.map((font) => ({
          name: font.name,
          value: `'${font.name}', sans-serif`,
          isCustom: true,
        }));
        setAvailableFonts([...DEFAULT_FONT_OPTIONS, ...customFontOptions]);

        // Update localStorage as cache
        localStorage.setItem("custom-fonts", JSON.stringify(savedCustomFonts));
      }

      // Load custom colors
      const savedCustomColors =
        firestoreSettings?.customColors ||
        (localStorage.getItem("custom-colors")
          ? JSON.parse(localStorage.getItem("custom-colors"))
          : null);

      if (savedCustomColors) {
        setCustomColors(savedCustomColors);
        setColorOptions([...defaultColorOptions, ...savedCustomColors]);
        localStorage.setItem(
          "custom-colors",
          JSON.stringify(savedCustomColors)
        );
      }

      // Load accent color
      const savedColor =
        firestoreSettings?.accentColor ||
        (localStorage.getItem("accent-color")
          ? JSON.parse(localStorage.getItem("accent-color"))
          : null);

      if (savedColor) {
        const allColors = savedCustomColors
          ? [...defaultColorOptions, ...savedCustomColors]
          : defaultColorOptions;
        const matchingColor = allColors.find(
          (c) => c.value === savedColor.value
        );
        if (matchingColor) {
          setSelectedColor(matchingColor);
          document.documentElement.style.setProperty(
            "--color-accent",
            matchingColor.value
          );
          document.documentElement.style.setProperty(
            "--color-accent-hover",
            matchingColor.hover
          );
          localStorage.setItem("accent-color", JSON.stringify(matchingColor));
        }
      }

      // Load font size
      const savedFontSize =
        firestoreSettings?.fontSize || localStorage.getItem("font-size");
      if (savedFontSize) {
        const size =
          typeof savedFontSize === "string"
            ? parseInt(savedFontSize)
            : savedFontSize;
        setFontSize(size);
        document.documentElement.style.fontSize = `${size}px`;
        localStorage.setItem("font-size", size.toString());
      }

      // Load font settings
      const savedFontSettings =
        firestoreSettings?.fontSettings ||
        (localStorage.getItem("font-settings")
          ? JSON.parse(localStorage.getItem("font-settings"))
          : null);

      if (savedFontSettings) {
        // Check in all available fonts (including custom ones)
        const allFonts = savedCustomFonts
          ? [
              ...DEFAULT_FONT_OPTIONS,
              ...savedCustomFonts.map((f) => ({
                name: f.name,
                value: `'${f.name}', sans-serif`,
                isCustom: true,
              })),
            ]
          : DEFAULT_FONT_OPTIONS;

        // Validate and set font settings
        const validatedSettings = {
          displayHeading:
            allFonts.find(
              (f) => f.name === savedFontSettings.displayHeading?.name
            ) || DEFAULT_FONT_SETTINGS.displayHeading,
          headings:
            allFonts.find((f) => f.name === savedFontSettings.headings?.name) ||
            DEFAULT_FONT_SETTINGS.headings,
          subheading:
            allFonts.find(
              (f) => f.name === savedFontSettings.subheading?.name
            ) || DEFAULT_FONT_SETTINGS.subheading,
          body:
            allFonts.find((f) => f.name === savedFontSettings.body?.name) ||
            DEFAULT_FONT_SETTINGS.body,
          ui:
            allFonts.find((f) => f.name === savedFontSettings.ui?.name) ||
            DEFAULT_FONT_SETTINGS.ui,
        };

        setFontSettings(validatedSettings);

        // Apply font settings to CSS
        document.documentElement.style.setProperty(
          "--font-display-heading",
          validatedSettings.displayHeading.value
        );
        document.documentElement.style.setProperty(
          "--font-heading",
          validatedSettings.headings.value
        );
        document.documentElement.style.setProperty(
          "--font-subheading",
          validatedSettings.subheading.value
        );
        document.documentElement.style.setProperty(
          "--font-body",
          validatedSettings.body.value
        );
        document.documentElement.style.setProperty(
          "--font-ui",
          validatedSettings.ui.value
        );

        localStorage.setItem(
          "font-settings",
          JSON.stringify(validatedSettings)
        );
      }
    };

    loadSettings();
  }, []);

  const handleFontUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    const validTypes = [
      "font/ttf",
      "font/otf",
      "application/x-font-ttf",
      "application/x-font-otf",
    ];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (
      !validTypes.includes(file.type) &&
      !["ttf", "otf"].includes(fileExtension)
    ) {
      toast.error("Please upload a valid TTF or OTF font file");
      return;
    }

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Font file size should be less than 5MB");
      return;
    }

    try {
      const reader = new FileReader();

      reader.onload = (e) => {
        const fontData = e.target.result;
        const fontName = file.name.replace(/\.(ttf|otf)$/i, "");

        // Check if font already exists
        if (customFonts.some((f) => f.name === fontName)) {
          toast.error("A font with this name already exists");
          return;
        }

        // Create @font-face rule
        const styleElement = document.createElement("style");
        styleElement.textContent = `
          @font-face {
            font-family: '${fontName}';
            src: url(${fontData});
            font-weight: normal;
            font-style: normal;
          }
        `;
        styleElement.setAttribute("data-font-name", fontName);
        document.head.appendChild(styleElement);

        // Add to custom fonts
        const newFont = {
          name: fontName,
          data: fontData,
          fileName: file.name,
          uploadDate: new Date().toISOString(),
        };

        const updatedCustomFonts = [...customFonts, newFont];
        setCustomFonts(updatedCustomFonts);

        // Update available fonts
        const newFontOption = {
          name: fontName,
          value: `'${fontName}', sans-serif`,
          isCustom: true,
        };
        setAvailableFonts([...availableFonts, newFontOption]);

        // Save to localStorage
        localStorage.setItem(
          "custom-fonts",
          JSON.stringify(updatedCustomFonts)
        );

        // Save to Firestore
        saveSettingsToFirestore({ customFonts: updatedCustomFonts });

        toast.success(`Font "${fontName}" uploaded successfully`);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading font:", error);
      toast.error("Failed to upload font");
    }

    // Reset file input
    event.target.value = "";
  };

  const removeCustomFont = (fontToRemove) => {
    // Remove @font-face rule
    const styleElements = document.querySelectorAll(
      `style[data-font-name="${fontToRemove.name}"]`
    );
    styleElements.forEach((el) => el.remove());

    // Update state
    const updatedCustomFonts = customFonts.filter(
      (f) => f.name !== fontToRemove.name
    );
    setCustomFonts(updatedCustomFonts);

    // Update available fonts
    const updatedAvailableFonts = availableFonts.filter(
      (f) => f.name !== fontToRemove.name
    );
    setAvailableFonts(updatedAvailableFonts);

    // Save to localStorage
    localStorage.setItem("custom-fonts", JSON.stringify(updatedCustomFonts));

    // Save to Firestore
    saveSettingsToFirestore({ customFonts: updatedCustomFonts });

    // If the removed font was used in any category, switch to defaults
    let settingsChanged = false;
    const newFontSettings = { ...fontSettings };

    if (fontSettings.displayHeading.name === fontToRemove.name) {
      newFontSettings.displayHeading = DEFAULT_FONT_SETTINGS.displayHeading;
      settingsChanged = true;
    }
    if (fontSettings.headings.name === fontToRemove.name) {
      newFontSettings.headings = DEFAULT_FONT_SETTINGS.headings;
      settingsChanged = true;
    }
    if (fontSettings.subheading.name === fontToRemove.name) {
      newFontSettings.subheading = DEFAULT_FONT_SETTINGS.subheading;
      settingsChanged = true;
    }
    if (fontSettings.body.name === fontToRemove.name) {
      newFontSettings.body = DEFAULT_FONT_SETTINGS.body;
      settingsChanged = true;
    }
    if (fontSettings.ui.name === fontToRemove.name) {
      newFontSettings.ui = DEFAULT_FONT_SETTINGS.ui;
      settingsChanged = true;
    }

    if (settingsChanged) {
      setFontSettings(newFontSettings);

      // Apply updated font settings to CSS
      document.documentElement.style.setProperty(
        "--font-display-heading",
        newFontSettings.displayHeading.value
      );
      document.documentElement.style.setProperty(
        "--font-heading",
        newFontSettings.headings.value
      );
      document.documentElement.style.setProperty(
        "--font-subheading",
        newFontSettings.subheading.value
      );
      document.documentElement.style.setProperty(
        "--font-body",
        newFontSettings.body.value
      );
      document.documentElement.style.setProperty(
        "--font-ui",
        newFontSettings.ui.value
      );

      localStorage.setItem("font-settings", JSON.stringify(newFontSettings));

      // Save font settings change to Firestore
      saveSettingsToFirestore({ fontSettings: newFontSettings });
    }

    toast.success(`Font "${fontToRemove.name}" removed`);
  };

  const applyColor = (color) => {
    setSelectedColor(color);
    document.documentElement.style.setProperty("--color-accent", color.value);
    document.documentElement.style.setProperty(
      "--color-accent-hover",
      color.hover
    );
    localStorage.setItem("accent-color", JSON.stringify(color));

    // Save to Firestore
    saveSettingsToFirestore({ accentColor: color });
  };

  const addCustomColor = () => {
    const newColor = {
      name: "Custom",
      value: customColor,
      hover: darkenColor(customColor),
      isCustom: true,
    };
    const updatedCustomColors = [...customColors, newColor];
    setCustomColors(updatedCustomColors);
    setColorOptions([...defaultColorOptions, ...updatedCustomColors]);
    localStorage.setItem("custom-colors", JSON.stringify(updatedCustomColors));
    setShowColorPicker(false);
    applyColor(newColor);

    // Save to Firestore
    saveSettingsToFirestore({
      customColors: updatedCustomColors,
      accentColor: newColor,
    });
  };

  const removeCustomColor = (colorToRemove) => {
    const updatedCustomColors = customColors.filter(
      (c) => c.value !== colorToRemove.value
    );
    setCustomColors(updatedCustomColors);
    setColorOptions([...defaultColorOptions, ...updatedCustomColors]);
    localStorage.setItem("custom-colors", JSON.stringify(updatedCustomColors));

    // If the removed color was selected, switch to default
    if (selectedColor.value === colorToRemove.value) {
      applyColor(defaultColorOptions[6]); // Rose
    }

    // Save to Firestore
    saveSettingsToFirestore({ customColors: updatedCustomColors });
  };

  const changeFontSize = (delta) => {
    const newSize = fontSize + delta;
    if (newSize >= 14 && newSize <= 24) {
      setFontSize(newSize);
      document.documentElement.style.fontSize = `${newSize}px`;
      localStorage.setItem("font-size", newSize.toString());

      // Save to Firestore
      saveSettingsToFirestore({ fontSize: newSize });
    }
  };

  const changeFontCategory = (category, fontName) => {
    const font = availableFonts.find((f) => f.name === fontName);
    if (font) {
      const newFontSettings = {
        ...fontSettings,
        [category]: font,
      };

      setFontSettings(newFontSettings);

      // Apply to CSS based on category
      const cssVariableMap = {
        displayHeading: "--font-display-heading",
        headings: "--font-heading",
        subheading: "--font-subheading",
        body: "--font-body",
        ui: "--font-ui",
      };

      document.documentElement.style.setProperty(
        cssVariableMap[category],
        font.value
      );
      localStorage.setItem("font-settings", JSON.stringify(newFontSettings));

      // Save to Firestore
      saveSettingsToFirestore({ fontSettings: newFontSettings });
    }
  };

  const resetSettings = () => {
    // Reset color to Rose
    const defaultColor = defaultColorOptions[6];
    applyColor(defaultColor);

    // Reset font size
    setFontSize(18);
    document.documentElement.style.fontSize = "18px";
    localStorage.removeItem("font-size");

    // Reset fonts to defaults
    setFontSettings(DEFAULT_FONT_SETTINGS);
    document.documentElement.style.setProperty(
      "--font-display-heading",
      DEFAULT_FONT_SETTINGS.displayHeading.value
    );
    document.documentElement.style.setProperty(
      "--font-heading",
      DEFAULT_FONT_SETTINGS.headings.value
    );
    document.documentElement.style.setProperty(
      "--font-subheading",
      DEFAULT_FONT_SETTINGS.subheading.value
    );
    document.documentElement.style.setProperty(
      "--font-body",
      DEFAULT_FONT_SETTINGS.body.value
    );
    document.documentElement.style.setProperty(
      "--font-ui",
      DEFAULT_FONT_SETTINGS.ui.value
    );
    localStorage.removeItem("font-settings");

    // Clear custom colors
    setCustomColors([]);
    setColorOptions(defaultColorOptions);
    localStorage.removeItem("custom-colors");

    // Clear custom fonts
    customFonts.forEach((font) => {
      const styleElements = document.querySelectorAll(
        `style[data-font-name="${font.name}"]`
      );
      styleElements.forEach((el) => el.remove());
    });
    setCustomFonts([]);
    setAvailableFonts(DEFAULT_FONT_OPTIONS);
    localStorage.removeItem("custom-fonts");

    // Reset all settings in Firestore
    saveSettingsToFirestore({
      accentColor: defaultColor,
      fontSize: 18,
      fontSettings: DEFAULT_FONT_SETTINGS,
      customColors: [],
      customFonts: [],
    });
  };

  const handleDeleteAllPosts = async () => {
    setIsDeleting(true);
    try {
      const postsSnapshot = await getDocs(collection(db, "posts"));
      const deletePromises = postsSnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);
      toast.success(`Deleted ${postsSnapshot.size} blog posts`);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting posts:", error);
      toast.error("Failed to delete posts");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAllBriefly = async () => {
    setIsDeleting(true);
    try {
      const brieflySnapshot = await getDocs(collection(db, "briefly"));
      const deletePromises = brieflySnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);
      toast.success(`Deleted ${brieflySnapshot.size} briefly thoughts`);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting briefly thoughts:", error);
      toast.error("Failed to delete briefly thoughts");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <SettingsContainer isOpen={isOpen}>
        <SettingsHeader>
          <SettingsTitle>Settings</SettingsTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </SettingsHeader>

        <SettingsContent>
          <Section>
            <SectionTitle>Accent Color</SectionTitle>
            <CurrentColor>
              <ColorSwatch color={selectedColor.value} />
              <ColorInfo>
                <ColorLabel>Current Color</ColorLabel>
                <ColorName>{selectedColor.name}</ColorName>
              </ColorInfo>
            </CurrentColor>
            <ColorOptions>
              {colorOptions.map((color, index) => (
                <ColorOptionWrapper
                  key={`${color.name}-${index}`}
                  onMouseEnter={() => setHoveredColor(`${color.name}-${index}`)}
                  onMouseLeave={() => setHoveredColor(null)}
                >
                  <Tooltip visible={hoveredColor === `${color.name}-${index}`}>
                    {color.name}
                  </Tooltip>
                  <ColorOption
                    color={color.value}
                    onClick={() => applyColor(color)}
                  />
                  {color.isCustom && (
                    <DeleteButton
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCustomColor(color);
                      }}
                    >
                      ×
                    </DeleteButton>
                  )}
                </ColorOptionWrapper>
              ))}
              <ColorOptionWrapper
                onMouseEnter={() => setHoveredColor("add-color")}
                onMouseLeave={() => setHoveredColor(null)}
              >
                <Tooltip visible={hoveredColor === "add-color"}>
                  Add custom color
                </Tooltip>
                <AddColorButton onClick={() => setShowColorPicker(true)}>
                  +
                </AddColorButton>
              </ColorOptionWrapper>
            </ColorOptions>
          </Section>

          <Section>
            <SectionTitle>Font Size</SectionTitle>
            <FontSizeControl>
              <FontSizeButton
                onClick={() => changeFontSize(-1)}
                disabled={fontSize <= 14}
              >
                −
              </FontSizeButton>
              <FontSizeDisplay>{fontSize}px</FontSizeDisplay>
              <FontSizeButton
                onClick={() => changeFontSize(1)}
                disabled={fontSize >= 24}
              >
                +
              </FontSizeButton>
            </FontSizeControl>
          </Section>

          <Section>
            <SectionTitle>Typography</SectionTitle>

            <FontCategorySection>
              <FontCategoryTitle>
                Display Headings (Main Titles)
              </FontCategoryTitle>
              <FontSelect
                value={fontSettings.displayHeading.name}
                onChange={(e) =>
                  changeFontCategory("displayHeading", e.target.value)
                }
              >
                {availableFonts.map((font) => (
                  <option key={font.name} value={font.name}>
                    {font.name}
                  </option>
                ))}
              </FontSelect>
              <FontPreview
                fontFamily={fontSettings.displayHeading.value}
                isDisplay
              >
                The Quick Brown Fox
              </FontPreview>
            </FontCategorySection>

            <FontCategorySection>
              <FontCategoryTitle>
                Headings (Sections & Subheadings)
              </FontCategoryTitle>
              <FontSelect
                value={fontSettings.headings.name}
                onChange={(e) => changeFontCategory("headings", e.target.value)}
              >
                {availableFonts.map((font) => (
                  <option key={font.name} value={font.name}>
                    {font.name}
                  </option>
                ))}
              </FontSelect>
              <FontPreview fontFamily={fontSettings.headings.value} isHeading>
                Chapter Title Example
              </FontPreview>
            </FontCategorySection>

            <FontCategorySection>
              <FontCategoryTitle>
                Page Subheadings (Descriptions & Taglines)
              </FontCategoryTitle>
              <FontSelect
                value={fontSettings.subheading.name}
                onChange={(e) =>
                  changeFontCategory("subheading", e.target.value)
                }
              >
                {availableFonts.map((font) => (
                  <option key={font.name} value={font.name}>
                    {font.name}
                  </option>
                ))}
              </FontSelect>
              <FontPreview fontFamily={fontSettings.subheading.value}>
                A descriptive subtitle that sets the mood
              </FontPreview>
            </FontCategorySection>

            <FontCategorySection>
              <FontCategoryTitle>
                Body Text (Paragraphs & Content)
              </FontCategoryTitle>
              <FontSelect
                value={fontSettings.body.name}
                onChange={(e) => changeFontCategory("body", e.target.value)}
              >
                {availableFonts.map((font) => (
                  <option key={font.name} value={font.name}>
                    {font.name}
                  </option>
                ))}
              </FontSelect>
              <FontPreview fontFamily={fontSettings.body.value}>
                This is how your body text will look in articles and content.
              </FontPreview>
            </FontCategorySection>

            <FontCategorySection>
              <FontCategoryTitle>
                UI Elements (Buttons & Interface)
              </FontCategoryTitle>
              <FontSelect
                value={fontSettings.ui.name}
                onChange={(e) => changeFontCategory("ui", e.target.value)}
              >
                {availableFonts.map((font) => (
                  <option key={font.name} value={font.name}>
                    {font.name}
                  </option>
                ))}
              </FontSelect>
              <FontPreview fontFamily={fontSettings.ui.value}>
                Button Text • Navigation • Labels
              </FontPreview>
            </FontCategorySection>

            <CustomFontSection>
              <FileInputWrapper>
                <FileInputLabel>
                  <input
                    type="file"
                    accept=".ttf,.otf"
                    onChange={handleFontUpload}
                  />
                  + Upload Custom Font (TTF/OTF)
                </FileInputLabel>
                <FontUploadInfo>Max file size: 5MB</FontUploadInfo>
              </FileInputWrapper>

              {customFonts.length > 0 && (
                <>
                  <SectionTitle
                    style={{
                      fontSize: "0.875rem",
                      marginTop: "1rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Custom Fonts
                  </SectionTitle>
                  <CustomFontsList>
                    {customFonts.map((font) => (
                      <CustomFontItem key={font.name}>
                        <CustomFontName
                          fontFamily={`'${font.name}', sans-serif`}
                        >
                          {font.name}
                        </CustomFontName>
                        <RemoveFontButton
                          onClick={() => removeCustomFont(font)}
                        >
                          Remove
                        </RemoveFontButton>
                      </CustomFontItem>
                    ))}
                  </CustomFontsList>
                </>
              )}
            </CustomFontSection>
          </Section>

          <Section>
            <ResetButton onClick={resetSettings}>Reset to Defaults</ResetButton>
          </Section>

          {isAuthorized && (
            <AdvancedSection>
              <CollapsibleHeader
                onClick={() => setShowAdvanced(!showAdvanced)}
                isOpen={showAdvanced}
              >
                Advanced Settings
                <ArrowIcon />
              </CollapsibleHeader>

              <CollapsibleContent isOpen={showAdvanced}>
                <DangerZone>
                  <DangerWarning>
                    <span className="icon">⚠️</span>
                    <div>
                      <strong>Danger Zone:</strong> These actions are
                      irreversible and will permanently delete data from the
                      database.
                    </div>
                  </DangerWarning>
                </DangerZone>

                <DangerButton
                  onClick={() => setShowDeleteConfirm("posts")}
                  disabled={isDeleting}
                >
                  Delete All Blog Posts
                </DangerButton>

                <DangerButton
                  onClick={() => setShowDeleteConfirm("briefly")}
                  disabled={isDeleting}
                >
                  Delete All Briefly Thoughts
                </DangerButton>
              </CollapsibleContent>
            </AdvancedSection>
          )}
        </SettingsContent>
      </SettingsContainer>

      <ColorPickerBackdrop
        isOpen={showColorPicker}
        onClick={() => setShowColorPicker(false)}
      />
      <ColorPickerModal isOpen={showColorPicker}>
        <ColorPickerTitle>Choose Custom Color</ColorPickerTitle>
        <ColorInput
          type="color"
          value={customColor}
          onChange={(e) => setCustomColor(e.target.value)}
        />
        <ColorPickerButtons>
          <PickerButton
            className="secondary"
            onClick={() => setShowColorPicker(false)}
          >
            Cancel
          </PickerButton>
          <PickerButton className="primary" onClick={addCustomColor}>
            Add Color
          </PickerButton>
        </ColorPickerButtons>
      </ColorPickerModal>

      {showDeleteConfirm && (
        <>
          <ConfirmBackdrop onClick={() => setShowDeleteConfirm(null)} />
          <ConfirmDialog>
            <ConfirmTitle>
              {showDeleteConfirm === "posts"
                ? "Delete All Blog Posts?"
                : "Delete All Briefly Thoughts?"}
            </ConfirmTitle>
            <ConfirmText>
              This action cannot be undone. All{" "}
              {showDeleteConfirm === "posts"
                ? "blog posts"
                : "briefly thoughts"}{" "}
              will be permanently deleted from the database.
            </ConfirmText>
            <ConfirmButtons>
              <ConfirmButton
                className="cancel"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </ConfirmButton>
              <ConfirmButton
                className="confirm"
                onClick={
                  showDeleteConfirm === "posts"
                    ? handleDeleteAllPosts
                    : handleDeleteAllBriefly
                }
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete All"}
              </ConfirmButton>
            </ConfirmButtons>
          </ConfirmDialog>
        </>
      )}
    </>
  );
};

export default Settings;
